/**
 * useBreadcrumbs Hook
 *
 * Automatically generates breadcrumb trails from the current route.
 * Works with the routing system to provide consistent navigation hierarchy.
 *
 * Features:
 * - Automatic breadcrumb generation from URL path
 * - Support for dynamic route parameters
 * - Integration with module icon system
 * - Custom breadcrumb override support
 *
 * @example
 * ```tsx
 * const MyPage = () => {
 *   const breadcrumbs = useBreadcrumbs({
 *     customBreadcrumbs: [
 *       { label: "Teams", path: "/teams" },
 *       { label: "Team Detail" }
 *     ]
 *   });
 *
 *   return <PageHeader breadcrumbs={breadcrumbs} />;
 * };
 * ```
 *
 * @see .github/copilot-instructions.md#components--ui
 */

import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { BreadcrumbItem } from 'components/PageHeader';
import { getModuleNameFromPath } from '../constants/moduleIcons';

interface UseBreadcrumbsOptions {
  /**
   * Custom breadcrumbs to override automatic generation
   */
  customBreadcrumbs?: BreadcrumbItem[];

  /**
   * Whether to include "Home" as the first breadcrumb
   * @default true
   */
  includeHome?: boolean;

  /**
   * Custom labels for specific path segments
   * Maps path segments to display labels
   */
  segmentLabels?: Record<string, string>;

  /**
   * Additional breadcrumbs to append after auto-generated ones
   */
  additionalBreadcrumbs?: BreadcrumbItem[];
}

/**
 * Default labels for common path segments
 */
const DEFAULT_SEGMENT_LABELS: Record<string, string> = {
  // Admin routes
  admin: 'Administration',
  'super-admins': 'Super Admins',
  tenant: 'Tenant',
  subscription: 'Subscription',
  sandbox: 'Sandbox',
  'tenant-realm': 'Tenant & Realm Management',

  // Teams routes
  teams: 'Teams',
  'teams-2': 'Teams 2.0',
  members: 'Members',
  'data-deletion-review': 'Data Deletion Review',

  // Tools routes
  tools: 'Tools',
  csm: 'CSM Tools',
  data: 'Data Management',
  'team-lifecycle': 'Team Lifecycle',

  // Integration routes
  integrations: 'Integrations',
  health: 'Health',
  logs: 'Logs',
  'pending-entity': 'Pending Entities',

  // Common actions
  profile: 'Profile',
  settings: 'Settings',
  edit: 'Edit',
  create: 'Create',
  view: 'View',

  // Pricing
  'pricing-calculator': 'Pricing Calculator',

  // Auth
  auth: 'Authentication',
  'sign-in': 'Sign In',
  'sign-up': 'Sign Up',
} as const;

/**
 * Convert a path segment to a readable label
 */
const formatSegmentLabel = (segment: string, customLabels?: Record<string, string>): string => {
  // Check custom labels first
  if (customLabels?.[segment]) {
    return customLabels[segment];
  }

  // Check default labels
  if (DEFAULT_SEGMENT_LABELS[segment]) {
    return DEFAULT_SEGMENT_LABELS[segment];
  }

  // If it's a number (likely an ID), return it as-is
  if (/^\d+$/.test(segment)) {
    return `ID: ${segment}`;
  }

  // Convert kebab-case or snake_case to Title Case
  return segment
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Generate breadcrumbs from URL path
 */
const generateBreadcrumbsFromPath = (
  pathname: string,
  params: Record<string, string | undefined>,
  segmentLabels?: Record<string, string>,
): BreadcrumbItem[] => {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];

  let currentPath = '';

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    currentPath += `/${segment}`;

    // Replace route params with actual values
    let displaySegment = segment;
    if (segment.startsWith(':')) {
      const paramName = segment.slice(1);
      displaySegment = params[paramName] || segment;
    }

    // Don't create breadcrumb for the last segment (current page)
    // It will be added separately without a link
    const isLast = i === segments.length - 1;

    breadcrumbs.push({
      label: formatSegmentLabel(displaySegment, segmentLabels),
      path: isLast ? undefined : currentPath,
    });
  }

  return breadcrumbs;
};

/**
 * Hook to generate breadcrumbs based on current route
 */
export const useBreadcrumbs = (options: UseBreadcrumbsOptions = {}): BreadcrumbItem[] => {
  const {
    customBreadcrumbs,
    includeHome = true,
    segmentLabels,
    additionalBreadcrumbs = [],
  } = options;

  const location = useLocation();
  const params = useParams();

  const breadcrumbs = useMemo(() => {
    // If custom breadcrumbs provided, use them
    if (customBreadcrumbs) {
      return includeHome ? [{ label: 'Home', path: '/' }, ...customBreadcrumbs] : customBreadcrumbs;
    }

    // Generate breadcrumbs from current path
    const generated = generateBreadcrumbsFromPath(location.pathname, params, segmentLabels);

    // Add home breadcrumb if enabled
    const result = includeHome ? [{ label: 'Home', path: '/' }, ...generated] : generated;

    // Add additional breadcrumbs
    return [...result, ...additionalBreadcrumbs];
  }, [
    location.pathname,
    params,
    customBreadcrumbs,
    includeHome,
    segmentLabels,
    additionalBreadcrumbs,
  ]);

  return breadcrumbs;
};

/**
 * Hook to get the current module name from the route
 */
export const useCurrentModule = (): string | null => {
  const location = useLocation();

  return useMemo(() => {
    return getModuleNameFromPath(location.pathname);
  }, [location.pathname]);
};

/**
 * Hook to generate breadcrumbs with module awareness
 * This hook integrates with the module icon system
 */
export const useModuleBreadcrumbs = (
  moduleName?: string,
  options: UseBreadcrumbsOptions = {},
): BreadcrumbItem[] => {
  const location = useLocation();
  const autoDetectedModule = useCurrentModule();
  const currentModule = moduleName || autoDetectedModule;

  // Call hook at top level (not inside useMemo)
  const standardBreadcrumbs = useBreadcrumbs(options);

  return useMemo(() => {
    if (options.customBreadcrumbs) {
      return standardBreadcrumbs;
    }

    // Build breadcrumbs with module name
    const baseBreadcrumbs: BreadcrumbItem[] =
      options.includeHome !== false ? [{ label: 'Home', path: '/' }] : [];

    if (currentModule) {
      // Add module as breadcrumb
      baseBreadcrumbs.push({
        label: currentModule,
        path: location.pathname.split('/').slice(0, 2).join('/') || '/',
      });
    }

    // Add additional segments if path is deeper
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length > 1) {
      let currentPath = `/${segments[0]}`;

      for (let i = 1; i < segments.length; i++) {
        currentPath += `/${segments[i]}`;
        const isLast = i === segments.length - 1;

        baseBreadcrumbs.push({
          label: formatSegmentLabel(segments[i], options.segmentLabels),
          path: isLast ? undefined : currentPath,
        });
      }
    }

    return [...baseBreadcrumbs, ...(options.additionalBreadcrumbs || [])];
  }, [standardBreadcrumbs, currentModule, location.pathname, options]);
};

export default useBreadcrumbs;
